// =============================================================================
// SimSense Platform — Custom Select Component [OT-219]
// =============================================================================
// Drop-in replacement for native <select> with full dark theme support.
// Usage: <Select value={v} onChange={setV} options={[{value:"a",label:"A"}]} />

import { useState, useRef, useEffect, useCallback } from "react";

export interface SelectOption {
  value: string;
  label: string;
  group?: string;
}

interface SelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  style?: React.CSSProperties;
  size?: "sm" | "md";
}

export function Select({ value, onChange, options, placeholder, style, size = "md" }: SelectProps) {
  const [open, setOpen] = useState(false);
  const [hlIndex, setHlIndex] = useState(-1);
  const ref = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const selected = options.find(o => o.value === value);

  // When dropdown opens, highlight the currently selected option
  useEffect(() => {
    if (open) {
      const idx = options.findIndex(o => o.value === value);
      setHlIndex(idx >= 0 ? idx : 0);
    }
  }, [open]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!open) return;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHlIndex(prev => (prev + 1) % options.length);
        break;
      case "ArrowUp":
        e.preventDefault();
        setHlIndex(prev => (prev - 1 + options.length) % options.length);
        break;
      case "Enter":
        e.preventDefault();
        if (hlIndex >= 0 && hlIndex < options.length) {
          onChange(options[hlIndex].value);
          setOpen(false);
        }
        break;
      case "Escape":
        e.preventDefault();
        setOpen(false);
        break;
    }
  }, [open, hlIndex, options, onChange]);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (open && listRef.current && hlIndex >= 0) {
      const items = listRef.current.querySelectorAll("[data-option-index]");
      const target = items[hlIndex] as HTMLElement;
      if (target) target.scrollIntoView({ block: "nearest" });
    }
  }, [open, hlIndex]);

  const pad = size === "sm" ? "6px 10px" : "10px 12px";
  const fontSize = size === "sm" ? "12px" : "13px";

  // Group options if any have group property
  const groups = new Map<string, SelectOption[]>();
  let hasGroups = false;
  for (const opt of options) {
    const g = opt.group || "";
    if (g) hasGroups = true;
    if (!groups.has(g)) groups.set(g, []);
    groups.get(g)!.push(opt);
  }

  // Build flat index mapping for grouped rendering
  let flatIndex = 0;

  return (
    <div ref={ref} style={{ position: "relative", ...style }}>
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        style={{
          width: "100%",
          padding: pad,
          background: "var(--bg)",
          border: `1px solid ${open ? "var(--accent)" : "var(--border)"}`,
          borderRadius: "6px",
          color: selected ? "var(--text1)" : "var(--text3)",
          fontSize,
          fontFamily: "inherit",
          fontWeight: 500,
          textAlign: "left",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "8px",
          transition: "border-color 0.15s",
          outline: "none",
        }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {selected?.label || placeholder || "Select..."}
        </span>
        <svg width="10" height="6" viewBox="0 0 10 6" fill="none" style={{ flexShrink: 0, transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s" }}>
          <path d="M1 1L5 5L9 1" stroke="var(--text3)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {/* Dropdown */}
      {open && (
        <div
          ref={listRef}
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            background: "var(--card)",
            border: "1px solid var(--border)",
            borderRadius: "6px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
            zIndex: 1000,
            maxHeight: "240px",
            overflowY: "auto",
            padding: "4px",
          }}
        >
          {hasGroups ? (
            Array.from(groups.entries()).map(([groupName, groupOpts]) => (
              <div key={groupName}>
                {groupName && (
                  <div style={{ padding: "6px 10px 4px", fontSize: "10px", color: "var(--text3)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    {groupName}
                  </div>
                )}
                {groupOpts.map(opt => {
                  const idx = flatIndex++;
                  return (
                    <OptionItem
                      key={opt.value}
                      opt={opt}
                      index={idx}
                      isActive={opt.value === value}
                      isHighlighted={idx === hlIndex}
                      fontSize={fontSize}
                      onSelect={() => { onChange(opt.value); setOpen(false); }}
                      onHover={() => setHlIndex(idx)}
                    />
                  );
                })}
              </div>
            ))
          ) : (
            options.map((opt, idx) => (
              <OptionItem
                key={opt.value}
                opt={opt}
                index={idx}
                isActive={opt.value === value}
                isHighlighted={idx === hlIndex}
                fontSize={fontSize}
                onSelect={() => { onChange(opt.value); setOpen(false); }}
                onHover={() => setHlIndex(idx)}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

function OptionItem({ opt, index, isActive, isHighlighted, fontSize, onSelect, onHover }: {
  opt: SelectOption;
  index: number;
  isActive: boolean;
  isHighlighted: boolean;
  fontSize: string;
  onSelect: () => void;
  onHover: () => void;
}) {
  const bg = isActive
    ? "rgba(var(--accent-rgb, 99,102,241), 0.15)"
    : isHighlighted
      ? "rgba(255,255,255,0.07)"
      : "transparent";

  return (
    <button
      type="button"
      data-option-index={index}
      onClick={onSelect}
      onMouseEnter={onHover}
      style={{
        display: "block",
        width: "100%",
        padding: "8px 10px",
        background: bg,
        border: "none",
        borderRadius: "4px",
        color: isActive ? "var(--accent)" : "var(--text1)",
        fontSize,
        fontFamily: "inherit",
        fontWeight: isActive ? 600 : 400,
        textAlign: "left",
        cursor: "pointer",
      }}
    >
      {opt.label}
    </button>
  );
}
